import React from 'react';
import { LegalPageLayout, LegalSection, LegalCallout } from '../components/LegalPageLayout';

const TOC = [
  { id: 'purpose', label: 'Purpose & Scope' },
  { id: 'infrastructure', label: 'Infrastructure Security' },
  { id: 'data', label: 'Data Protection' },
  { id: 'access', label: 'Access Control & Tenant Isolation' },
  { id: 'appsec', label: 'Application Security' },
  { id: 'monitoring', label: 'Monitoring & Incident Response' },
  { id: 'payments', label: 'Payment Security' },
  { id: 'org', label: 'Organizational Security' },
  { id: 'customer', label: 'Your Responsibilities' },
  { id: 'changes', label: 'Changes & Contact' },
];

const SecurityPolicyPage = () => (
  <LegalPageLayout title="Security Policy" lastUpdated="August 5, 2026" toc={TOC}>
    <LegalSection id="purpose" number={1} title="Purpose & Scope">
      <p>
        This Security Policy describes the administrative, technical, and organizational measures
        BookingFlows uses to protect the platform and the data processed through it. It applies to the
        BookingFlows web application, public APIs, embeddable booking widgets, and related services.
        For a plain-English overview of our security capabilities, see our{' '}
        <a href="/security">Trust &amp; Security page</a>. For how we handle personal data, see our{' '}
        <a href="/privacy">Privacy Policy</a>.
      </p>
    </LegalSection>

    <LegalSection id="infrastructure" number={2} title="Infrastructure Security">
      <ul>
        <li>The platform is hosted with established cloud infrastructure providers in access-controlled, professionally managed data centers.</li>
        <li>All traffic to and from the platform is encrypted in transit using TLS.</li>
        <li>Production systems are separated from development and testing environments.</li>
        <li>Infrastructure access is restricted to authorized personnel and protected by strong authentication.</li>
      </ul>
    </LegalSection>

    <LegalSection id="data" number={3} title="Data Protection">
      <ul>
        <li>Data is encrypted in transit (TLS) and at rest.</li>
        <li>Encrypted backups of platform data are taken daily and stored separately from the live environment.</li>
        <li>Sensitive credentials (passwords, API secrets, integration tokens) are stored hashed or encrypted — never in plaintext.</li>
        <li>Data retention follows the schedules described in our <a href="/privacy">Privacy Policy</a>.</li>
      </ul>
    </LegalSection>

    <LegalSection id="access" number={4} title="Access Control & Tenant Isolation">
      <ul>
        <li>BookingFlows is a multi-tenant platform with <strong>organization-scoped data isolation enforced server-side</strong> on every request — one Operator&apos;s data is never accessible to another.</li>
        <li>Role-based access control (RBAC) lets Operators grant team members only the permissions their role requires.</li>
        <li>Two-factor authentication is available for Operator accounts, and administrative platform access requires multi-step verification.</li>
        <li>API access uses scoped keys transmitted via request headers only, with per-key rate limiting.</li>
      </ul>
    </LegalSection>

    <LegalSection id="appsec" number={5} title="Application Security">
      <ul>
        <li>Every code change is checked by automated static analysis and security scanning before release.</li>
        <li>Third-party dependencies are continuously scanned against known vulnerability databases and patched promptly when fixes are published.</li>
        <li>Releases must pass an automated build-and-verification pipeline; failing checks block deployment.</li>
        <li>An automated test suite regularly exercises real platform flows — including authentication, bookings, and payments — to catch regressions.</li>
      </ul>
    </LegalSection>

    <LegalSection id="monitoring" number={6} title="Monitoring & Incident Response">
      <ul>
        <li>The platform is monitored continuously — performance, error rates, and availability — with automated alerting.</li>
        <li>Application errors are tracked in real time so issues can be identified and remediated quickly.</li>
        <li>In the event of a security incident affecting your data, we will investigate promptly, take corrective action, and notify affected Operators without undue delay, consistent with applicable law.</li>
      </ul>
    </LegalSection>

    <LegalSection id="payments" number={7} title="Payment Security">
      <p>
        All payments are processed by <strong>Stripe</strong>, a PCI DSS Level 1 certified payment
        processor. Card numbers are entered directly into Stripe-hosted fields and{' '}
        <strong>never touch BookingFlows servers</strong>. We store only non-sensitive payment
        references (such as transaction identifiers and payment status).
      </p>
    </LegalSection>

    <LegalSection id="org" number={8} title="Organizational Security">
      <ul>
        <li>Access to production systems and customer data is limited to personnel who need it to operate the service.</li>
        <li>Administrative actions on the platform are logged.</li>
        <li>Security practices are reviewed on an ongoing basis and improved as the platform evolves.</li>
      </ul>
    </LegalSection>

    <LegalSection id="customer" number={9} title="Your Responsibilities">
      <p>Security is shared. Operators are responsible for:</p>
      <ul>
        <li>Using strong, unique passwords and enabling two-factor authentication;</li>
        <li>Managing team member roles and removing access when staff leave;</li>
        <li>Keeping API keys confidential and rotating them if exposure is suspected;</li>
        <li>Ensuring their own devices and networks are secure.</li>
      </ul>
    </LegalSection>

    <LegalSection id="changes" number={10} title="Changes & Contact">
      <p>
        We may update this policy as our practices evolve; the effective date above reflects the latest
        revision. Questions about this policy can be raised through our{' '}
        <a href="/support#tickets">Support Center</a>. To report a suspected vulnerability, please use our{' '}
        <a href="/vulnerability-disclosure">Vulnerability Disclosure Policy</a>.
      </p>
      <LegalCallout>
        <p>
          BookingFlows does not currently claim third-party certifications such as SOC 2 or ISO 27001.
          Formal audit certification is on our roadmap; we describe our current, verifiable practices
          honestly rather than borrowing badges.
        </p>
      </LegalCallout>
    </LegalSection>
  </LegalPageLayout>
);

export default SecurityPolicyPage;
